import React from 'react';
import * as Styles from '../styles';
import { newEmojis } from '../../../utils/emojis';
import { useOutsideClick } from '../../../hooks/useOutsideClick';
import { parseEmoji } from '../../../utils/parseEmoji';
import { checkLengthStr } from '../../../utils/checkLengthStr';
import { Loader } from '../../ui/Loader';

interface InputChatProps {
  isEnable: boolean;
  loading?: boolean;
  sendMessage: (message: string) => void;
}

const SYMB_LENGTH = 255;

export const InputChat: React.FC<InputChatProps> = React.memo(({ isEnable, loading, sendMessage }) => {
  const [myMessage, setMyMessage] = React.useState('');
  const [isShowEmoji, setIsShowEmoji] = React.useState(false);
  const [mountMessage, setMountMessage] = React.useState(false);
  const [limitError, setLimitError] = React.useState(false);
  const refMe = React.useRef<HTMLDivElement | null>(null);
  const { ref } = useOutsideClick(() => setIsShowEmoji(false));

  const handleSetError = React.useCallback(
    (length: number) => {
      if (length >= SYMB_LENGTH) {
        setLimitError(true);
      } else if (limitError) {
        setLimitError(false);
      }
    },
    [limitError]
  );

  React.useEffect(() => {
    if (refMe.current && mountMessage && (refMe.current.textContent?.length || 0) <= SYMB_LENGTH) {
      refMe.current.innerHTML = myMessage;
      setMountMessage(false);
      handleSetError(refMe.current.textContent?.length || 0);
    }
  }, [myMessage, setMountMessage, handleSetError, mountMessage]);

  const addEmoji = (em: { name: string; link: string; code: string }) => {
    setMyMessage((mes) => mes + ` <img style="width: 16px; height: 16px" src="${em.link}" alt="${em.name}">`);
    setIsShowEmoji(false);
  };

  const handleInput = (e: React.KeyboardEvent<HTMLDivElement>) => {
    setMyMessage(e.currentTarget.innerHTML as string);
    handleSetError(e.currentTarget.textContent?.length || 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      (refMe.current?.textContent?.length || 0) > SYMB_LENGTH - 1 &&
      ![
        'Backspace',
        'ArrowRight',
        'ArrowLeft',
        'ArrowUp',
        'ArrowDown',
        'Delete',
        'ShiftRight',
        'ShiftLeft',
        'Enter',
      ].includes(e.code)
    ) {
      e.preventDefault();
    }

    if (e.code === 'Enter' && !e.shiftKey) {
      setTimeout(() => {
        onSend();
      }, 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const text = e.clipboardData.getData('text');
    const length = text.length + (refMe.current?.textContent?.length || 0);
    const remained = SYMB_LENGTH - (refMe.current?.textContent?.length || 0);
    if (length > SYMB_LENGTH) {
      const selection = window.getSelection();
      if (!selection?.rangeCount) return e.preventDefault();
      selection.deleteFromDocument();
      const result = text.slice(0, remained);
      selection.getRangeAt(0).insertNode(document.createTextNode(result));
      handleSetError(e.currentTarget.textContent?.length || 0);
      e.preventDefault();
    }
  };

  const onSend = () => {
    let mes = myMessage.replaceAll('<br>', '\n').replace(/<\/?[^>]+(>|$)/g, '');
    let testMes = mes.replaceAll('\n', '');
    if (!testMes) return null;
    const parseMessage = parseEmoji(mes, true);
    const message = checkLengthStr(parseMessage);
    sendMessage(message);
    setMyMessage('');
    setMountMessage(true);
    setLimitError(false);
  };

  return (
    <Styles.InputWrapperS>
      <Styles.ChatField>
        <Styles.ChatInput
          ref={refMe}
          contentEditable={isEnable}
          suppressContentEditableWarning={true}
          data-text="Ваше повiдомлення"
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
        />
      </Styles.ChatField>
      <Styles.FootersWrapperS>
        <Styles.WrappTool>
          {isShowEmoji ? (
            <Styles.SmilesWrapperS ref={ref}>
              {newEmojis.map((em, i) => (
                <div
                  onClick={() => {
                    if (isEnable) {
                      addEmoji(em);
                      setMountMessage(true);
                    }
                  }}
                  key={`${em.name}-${i}`}
                >
                  <img src={em.link} alt={em.name} />
                </div>
              ))}
            </Styles.SmilesWrapperS>
          ) : null}
          {loading ? <Loader /> : null}
          <Styles.BtnS onClick={() => setIsShowEmoji(true)}>
            <i className="bi bi-emoji-smile" />
          </Styles.BtnS>
          <Styles.Hint isError={limitError}>
            Уведено {refMe.current?.textContent?.length || 0} із {SYMB_LENGTH} символів
          </Styles.Hint>
        </Styles.WrappTool>
        <div>
          <Styles.BtnS onClick={onSend}>
            <i className="ion-android-send" />
          </Styles.BtnS>
        </div>
      </Styles.FootersWrapperS>
    </Styles.InputWrapperS>
  );
});
